import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol, ToastController
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { 
    MarvButtonComponent, MarvInputDateComponent, MarvInputTimeComponent, MarvCheckboxComponent,
    MarvMessageComponent, MarvSectionTitleComponent
} from '@marv/components';
import { MarvDateService } from '@marv/services';
import { StoreCheckService } from '@app/services';
import { StoreCheck, StoreCheckUpdateObjective, StoreCheckUpdateDto } from '@app/models';

export class StoreCheckChangeEvent {
    statusUpdate: boolean;
    showQuestionnaire: boolean;
}

@Component({
    selector: 'app-permanent-planning-storecheck',
    templateUrl: './storecheck.component.html',
    styleUrls: ['./storecheck.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvInputDateComponent), forwardRef(() => MarvInputTimeComponent),
        forwardRef(() => MarvCheckboxComponent), forwardRef(() => MarvMessageComponent), forwardRef(() => MarvSectionTitleComponent)        
    ]
})
export class PermanentPlanningStorecheckComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'storeCheckId', required : true}) storeCheckId: number;
    @Output() onChangeStoreCheck = new EventEmitter<StoreCheckChangeEvent>();
    
    isFetchingSave: boolean = false;    
    storeCheck : StoreCheck|null = null;
    storeObjectives: Array<StoreCheckUpdateObjective> = [];
    permanentPlanningStoreCheckForm: FormGroup = new FormGroup({
        day: new FormControl(null, [Validators.required]),
        startHour: new FormControl(null, [Validators.required]),
        duration: new FormControl(null, [Validators.required])
    });
    
    constructor(
        public translateService: TranslateService,
        private toastController: ToastController,
        private storeCheckService: StoreCheckService
    ) { }

    ngOnInit() {}
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['storeCheckId']){
            await this.loadStoreCheck();
        }
    }
    
    async loadStoreCheck(){
        let storeCheck : StoreCheck|null = null;        
        this.removeFormControls();
        this.storeObjectives = [];
        let values: any = {
            day: null,
            startHour: null,
            duration: null
        };
        if(this.storeCheckId && this.operationId){
            storeCheck = await this.storeCheckService.loadStoreCheck(this.operationId, this.storeCheckId);
            if(storeCheck){
                values.day = MarvDateService.ToDate(storeCheck.visitDate);
                values.startHour = MarvDateService.ToTime(new Date(storeCheck.plannedStartTime));
                values.duration = MarvDateService.MinutesToStr(storeCheck.durationInMin);                
                this.storeObjectives = await this.storeCheckService.loadStoreObjectives(storeCheck.contextId, storeCheck.storeId, storeCheck.salesRepId);  
                for(let objective of this.storeObjectives){
                    objective.checked = (storeCheck.objectives.find(o => o.id === objective.id) !== undefined);
                    values['objective_'+objective.id] = (objective.mandatory || objective.checked);
                }    
                this.addFormControls();          
            }
        }
        this.storeCheck = storeCheck;    
        this.permanentPlanningStoreCheckForm.setValue(values);
    }
    
    removeFormControls(){
        for(let objective of this.storeObjectives){
            this.permanentPlanningStoreCheckForm.removeControl('objective_'+objective.id, {emitEvent: false});
        }
    }
    
    addFormControls(){
        for(let objective of this.storeObjectives){
            this.permanentPlanningStoreCheckForm.addControl('objective_'+objective.id, new FormControl(objective.mandatory || objective.checked), {emitEvent: false});
        }
    }
    
    dateOrDurationChanged() : boolean{
        let newDuration = MarvDateService.StrToMinutes(this.permanentPlanningStoreCheckForm.controls['duration'].value);
        let newPlannedStartTime = this.permanentPlanningStoreCheckForm.controls['startHour'].value.getTime();
        return (this.storeCheck !== null && (this.storeCheck.durationInMin !== newDuration || this.storeCheck.plannedStartTime !== newPlannedStartTime));          
    }
    
    getObjectivesChecked(): Array<number> {
        let objectivesIds: Array<number> = [];
        for(let objective of this.storeObjectives){
            if(objective.mandatory || this.permanentPlanningStoreCheckForm.controls['objective_'+objective.id].value){
                objectivesIds.push(objective.id);
            }
        }
        return objectivesIds;
    } 

    async save(){
        if(this.storeCheck 
            && (!this.storeCheck.hasCoachingSession || !this.dateOrDurationChanged())){
             
            if(this.permanentPlanningStoreCheckForm.valid){      

                this.isFetchingSave = true;     
                                   
                let objectivesIds = this.getObjectivesChecked();
                
                let day = this.permanentPlanningStoreCheckForm.controls['day'].value;               
                let refDate = MarvDateService.FromDate(day);                
                let minutesStart = MarvDateService.StrToMinutes(this.permanentPlanningStoreCheckForm.controls['startHour'].value)
                day.setMinutes(minutesStart);
                
                if(refDate){
                    let data: StoreCheckUpdateDto = {
                        id: this.storeCheck.id,
                        plannedStartDateTime: day.getTime(),
                        duration: MarvDateService.StrToMinutes(this.permanentPlanningStoreCheckForm.controls['duration'].value),
                        plannedObjectives: objectivesIds,
                        refDate: refDate
                    }

                    let statusUpdate = await this.storeCheckService.updateStoreCheck(this.storeCheck.contextId, this.storeCheck.id, data);

                    if(statusUpdate){    
                        this.onChangeStoreCheck.emit({statusUpdate: true, showQuestionnaire: (objectivesIds.length > 0)});                        
                        this.resetForm();
                        const toast = await this.toastController.create({
                            message: this.translateService.instant('MSG_SUCCESS_SAVE_STORECHECK'),
                            duration: 6000,
                            position: 'top',
                            color: 'success'
                        });
                        await toast.present();
                    }
                    else{
                        const toast = await this.toastController.create({
                            message: this.translateService.instant('MSG_ERROR_SAVE_STORECHECK'),
                            duration: 6000,
                            position: 'top',
                            color: 'danger'
                        });
                        await toast.present();
                    }
                }

                this.isFetchingSave = false;
            }
            else{
                for (var controlName in this.permanentPlanningStoreCheckForm.controls) {
                    if(!this.permanentPlanningStoreCheckForm.controls[controlName].valid) {
                        this.permanentPlanningStoreCheckForm.controls[controlName].markAsTouched();
                    }
                }
            }
        }
    }
    
    cancel(){
        this.resetForm();
        this.onChangeStoreCheck.emit({statusUpdate: false, showQuestionnaire: false});
    }
    
    resetForm() {
        this.permanentPlanningStoreCheckForm.reset();
    }

}
